import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { TsRestClient } from '@eluve/api-client-provider';
import { SessionUnavailableError } from '@eluve/eluve-errors';
import { Logger } from '@eluve/logger-main';
import { isAppointmentNoteSigned } from '@eluve/shared-gql-operations';

import { GenerateNoteEvent, TaskHandler } from '../types';

/**
 * One manifest file sent to the server at completion: the name plus the
 * finalize-time diagnostics (size, packet count, write window) used to cancel
 * empty files and detect dropped audio. [ENG-4717]
 */
export type ManifestFilePayload = {
  fileName: string;
  byteSize: number | null;
  packetCount: number | null;
  recordingStartedAt: string | null;
  firstPacketAt: string | null;
  lastPacketAt: string | null;
  /**
   * Degraded write-ahead backup: chunking stopped mid-segment and this file
   * is the segment's only complete audio — the server must protect it during
   * reconciliation. Absent/false for normal chunk files.
   */
  wasDegraded?: boolean | null;
};

/**
 * Loads the durable local manifest of audio files this device produced for an
 * appointment. Returns an empty array when no manifest exists (rare cross-device
 * case) — the server then performs no manifest reconciliation. [ENG-4717]
 */
export type LoadManifestFiles = (
  appointmentId: string,
) => Promise<ManifestFilePayload[]>;

/**
 * Asks the local file-sync to run an upload scan immediately. Called at
 * completion when the server reports files still uploading so they re-push
 * without waiting for the next periodic interval. No-op where there is no
 * periodic sync (e.g. the extension offscreen handlers). [ENG-4717]
 */
export type RequestFileSync = () => void;

export const generateNoteHandler = ({
  client,
  apiClient,
  logger,
  loadManifestFiles,
  requestFileSync,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  apiClient: TsRestClient | null;
  logger: Logger;
  loadManifestFiles?: LoadManifestFiles;
  requestFileSync?: RequestFileSync;
}): TaskHandler<GenerateNoteEvent> => {
  return async (task, context) => {
    if (!apiClient) {
      logger.error('API Client is not available for generating note', {
        appointmentId: task.appointmentId,
      });
      throw new SessionUnavailableError('API client not available');
    }

    const appointmentId = task.appointmentId;
    const userCompletedAt = task?.userCompletedAt ?? new Date().toISOString();
    const shouldOverrideExistingSummary =
      task.shouldOverrideExistingSummary ?? false;
    const tenantId = context.tenantId;
    const signal = context.signal;

    logger.info(
      `Attempting to generate summary note for appointment ${appointmentId}`,
      { appointmentId, tenantId, shouldOverrideExistingSummary },
    );

    // Signing is terminal: completing/regenerating a signed appointment would
    // re-fire summarization against a locked note, which the DB rejects and
    // then pages (ENG-5258). The backend guard is authoritative; this stops the
    // extension from doing the work (complete + override summarize) and gives
    // the provider a clear signal. Fail-open — a check failure resolves to
    // "not signed" so a transient error never blocks a legitimate note.
    const isSigned = await isAppointmentNoteSigned({
      client,
      appointmentId,
      tenantId,
      logger,
    });
    if (isSigned) {
      logger.info(
        'Skipping note generation: appointment note is already signed',
        { appointmentId, tenantId, shouldOverrideExistingSummary },
      );
      return;
    }

    // Build the manifest of files this device actually produced. Best-effort:
    // a failure (or no local manifest) falls through to an empty manifest, and
    // the server skips reconciliation rather than blocking.
    let manifest: { files: ManifestFilePayload[] } | undefined;
    if (loadManifestFiles) {
      try {
        const files = await loadManifestFiles(appointmentId);
        if (files.length > 0) {
          manifest = { files };
        }
      } catch (error) {
        logger.warn('Failed to load local manifest; completing without it', {
          appointmentId,
          error,
        });
      }
    }

    // Complete the appointment server-side: reconcile audio slots against the
    // manifest (cancel phantoms), THEN transition to COMPLETED, which fires the
    // status trigger that schedules note generation. Reconciliation must precede
    // the transition so the readiness gate doesn't see dangling phantom slots.
    const response = await apiClient.appointments.completeAppointment({
      params: { tenantId, appointmentId },
      body: { userCompletedAt, ...(manifest ? { manifest } : {}) },
      fetchOptions: { signal },
    });

    if (response.status !== 200) {
      logger.error('Failed to complete appointment', {
        appointmentId,
        tenantId,
        status: response.status,
      });
      throw new Error(
        `Failed to complete appointment (status ${response.status})`,
      );
    }

    // Optimistic cache flip so the UI reflects completion immediately (the
    // legacy GraphQL mutation used to do this via its normalized write). A no-op
    // if the appointment isn't in the cache.
    client.cache.modify({
      id: client.cache.identify({
        __typename: 'Appointments',
        id: appointmentId,
      }),
      fields: {
        status: () => 'COMPLETED',
        userCompletedAt: () => userCompletedAt,
      },
    });

    const { missingFiles, reconciled } = response.body;
    if (missingFiles.length > 0) {
      // These manifest files aren't AVAILABLE server-side yet — they're still
      // uploading. Kick the upload sync now (instead of waiting for its next
      // interval) so they re-push; generation proceeds automatically once they
      // finalize. [ENG-4717]
      requestFileSync?.();
      logger.info('Appointment completed; waiting on uploads before note gen', {
        appointmentId,
        missingCount: missingFiles.length,
        missingFiles: missingFiles.map((f) => f.fileName),
        cancelled: reconciled.cancelled,
        matched: reconciled.matched,
      });
    }

    // Override/regen path is unchanged: the COMPLETED→COMPLETED transition does
    // not re-fire the trigger, so force summarization explicitly.
    if (shouldOverrideExistingSummary) {
      await apiClient.llm.summarizeAppointment({
        body: {},
        params: {
          tenantId,
          appointmentId,
        },
        fetchOptions: { signal },
      });

      logger.info(
        'Successfully triggered note generation with override support',
        {
          appointmentId,
          tenantId,
          shouldOverrideExistingSummary,
        },
      );
    }
  };
};
